import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { cancelSessionResume, resumeSession } from '../services/sessionResume';

export default function SessionResumeDialog() {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    const onExpired = () => {
      setPassword('');
      setError('');
      setOpen(true);
    };
    window.addEventListener('session-expired', onExpired);
    return () => window.removeEventListener('session-expired', onExpired);
  }, []);

  const closeWithoutSaving = () => {
    setOpen(false);
    cancelSessionResume();
  };

  const signIn = async () => {
    const email = user?.email || user?.Email;
    if (!email) {
      setError('Sign in again from the login page.');
      return;
    }
    setSigningIn(true);
    setError('');
    try {
      const result = await login(email, password, { keepPage: true });
      if (!result?.success) {
        setError(result?.message || 'Sign in failed.');
        return;
      }
      if (result.requiresPasswordChange) {
        setOpen(false);
        cancelSessionResume('Change your password, then save this form again.');
        return;
      }
      setOpen(false);
      resumeSession();
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={signingIn ? undefined : closeWithoutSaving}
      maxWidth="xs"
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle>Sign in to keep this form</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Your session expired. Sign in again and the save will retry. This form stays open.
        </Typography>
        <TextField
          label="Email"
          value={user?.email || user?.Email || ''}
          fullWidth
          margin="normal"
          disabled
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          fullWidth
          margin="normal"
          autoFocus
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !signingIn) signIn();
          }}
        />
        {error ? (
          <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={closeWithoutSaving} disabled={signingIn}>Cancel</Button>
        <Button variant="contained" onClick={signIn} disabled={signingIn || !password}>
          {signingIn ? 'Signing in...' : 'Sign in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
